import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../css/common/ExplanationButton.css';
import DsModal from './DsModal';

const VIEWPORT_MARGIN = 12;
const POPOVER_WIDTH = 280;
const POPOVER_MAX_HEIGHT = 320;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const canUseDocument = () => typeof document !== 'undefined' && typeof window !== 'undefined';

const renderContent = (body, children) => {
  if (children) return children;
  if (body == null) return null;
  return <p className="explanation-content__body">{body}</p>;
};

export default function ExplanationButton({
  title = '解説',
  body,
  children,
  variant = 'popover',
  ariaLabel,
  className = '',
  buttonClassName = '',
  panelClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!canUseDocument() || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    const maxHeight = Math.min(POPOVER_MAX_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2);
    const preferredLeft = rect.left + rect.width / 2 - width / 2;
    const left = clamp(preferredLeft, VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    const belowTop = rect.bottom + 8;
    const aboveTop = rect.top - maxHeight - 8;
    const fitsBelow = belowTop + maxHeight <= window.innerHeight - VIEWPORT_MARGIN;
    const top = fitsBelow
      ? belowTop
      : clamp(aboveTop, VIEWPORT_MARGIN, window.innerHeight - maxHeight - VIEWPORT_MARGIN);
    setPosition({ left, top, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (open && variant === 'popover') {
      updatePosition();
    }
  }, [open, updatePosition, variant]);

  useEffect(() => {
    if (!open || !canUseDocument()) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const handleLayoutChange = () => {
      if (variant === 'popover') updatePosition();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('scroll', handleLayoutChange, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('scroll', handleLayoutChange, true);
    };
  }, [open, updatePosition, variant]);

  const content = renderContent(body, children);
  const label = ariaLabel || `${title}の解説ボタン`;
  const portalRoot = canUseDocument() ? document.body : null;

  const popover = open && variant === 'popover' && portalRoot
    ? createPortal(
        <div
          ref={panelRef}
          className={`explanation-popover ${panelClassName}`.trim()}
          role="region"
          aria-label={`${title}の解説`}
          style={{
            left: position ? `${position.left}px` : undefined,
            top: position ? `${position.top}px` : undefined,
            width: position ? `${position.width}px` : undefined,
            maxHeight: position ? `${position.maxHeight}px` : undefined,
          }}
        >
          <div className="explanation-content__head">
            <strong>{title}</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="解説を閉じる">
              ×
            </button>
          </div>
          <div className="explanation-content__main">{content}</div>
        </div>,
        portalRoot
      )
    : null;

  const modal = open && variant === 'modal' && portalRoot
    ? (
        <DsModal
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          ariaLabel={`${title}の解説`}
          className={`explanation-modal ${panelClassName}`.trim()}
        >
          <div ref={panelRef} className="explanation-content__main">{content}</div>
        </DsModal>
      )
    : null;

  return (
    <span className={`explanation-button-wrap ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        className={`explanation-button ${buttonClassName}`.trim()}
        aria-label={label}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        ?
      </button>
      {popover}
      {modal}
    </span>
  );
}
